import { Cache } from "./Cache";
import { stringUtils, globalUtils, coreUtils, NextValueFn } from "./util";
import { templates } from "./templates";
import { XMLHttpRequestProcessed } from "@otto-ec/global-resources";
import Mustache from "mustache";
import { sendEventToTracking, sendTrackingDataForNextPI, tracking } from "./tracking";
import { TrackingData } from "./SearchForm";

type JSONQuery = { q: string };
type AttributResponse = string | null;
type Keyword = { target: string; term: string; visual: string };
type Suggestions = { keywords: Keyword[]; suggestPos: NextValueFn };

const TEMPLATE_NAME = "searchSuggest";
const headerElementClass = "js_find_header";
const LAYER_CLASS_SELECTOR = ".js_squirrel_searchSuggestionsContainer";
const SEARCH_CONTAINER_CLASS_SELECTOR = ".js_squirrel_searchContainer";
const SEARCH_WRAPPER_CLASS_SELECTOR = ".squirrel_searchbarWrapper";
const SEARCH_FIELD_CLASS_SELECTOR = ".js_squirrel_searchbar__input";
const SEARCH_FORM_CLASS_SELECTOR = ".js_squirrel_searchForm";
const SEARCH_TERM_CLASS = ".js_squirrel_searchSuggest__term";
const SUGGEST_TERM_SELECTOR = ".js_squirrel_searchSuggest";
const ARROW_CLASS_SELECTOR = ".js_squirrel_searchSuggest__arrow";
const TRACK_MODIFIED_CLASS = "san_track_modified";
const TRACK_SUGGEST_CLASS = "san_track_suggest";
const SEARCHBAR_WRAPPER_VISIBLE_SUGGESTIONS_CLASS = "squirrel_searchbarWrapper--visibleSuggestions";
const ADAPTED_SPRINGBREAK_CLASS = "find_header--adaptedSpringBreak";
const DATA_POS = "data-suggestpos";
const DATA_TERM = "data-term";
const DATA_TARGET = "data-target";
const DATA_SUGGEST_URL = "data-suggestserveruri";
const DATA_SUGGEST_SEARCH_TERM = "data-suggest-search-term";
const ENTER_KEY = 13;
const ESC_KEY = 27;
const ARROW_KEY_UP = 38;
const ARROW_KEY_DOWN = 40;
let headerElement: Element;
let searchbarWrapper: HTMLDivElement;
let searchForm: HTMLFormElement;
let searchField: HTMLInputElement;
let suggestLayer: HTMLDivElement;
let oldSearchTerm: string | undefined;
let renderCache: Cache;
let suggestCache = {};

/**
 * Updates the suggest layer if the search field obtains focus. Needed after click on arrow.
 *
 * @param evt
 */
export function _focusSearchFieldAction(evt: Event) {
  // TODO
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const searchTerm: string = stringUtils.trimLeft(window.o_util.event.getTarget(evt).value);
  if (searchTerm !== "") {
    if (!headerElement.classList.contains(ADAPTED_SPRINGBREAK_CLASS)) {
      if (suggestLayer.innerHTML !== "" && !!oldSearchTerm) {
        _suggest(searchTerm);
      }
    } else {
      _suggest(searchTerm);
    }
  }
}

/**
 * Used for updating the search field on arrows
 * @param evt
 */
export function _clickArrowAction(evt: Event) {
  // TODO
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const target = Boolean(this.parentNode).valueOf() ? this.parentNode : evt.srcElement.offsetParent; // different structure in IE8
  const term = target.querySelector(SEARCH_TERM_CLASS);
  const searchTerm = term.getAttribute(DATA_TERM);
  const position = term.getAttribute(DATA_POS);

  window.o_util.event.stop(evt);

  _setSuggest(position);
  _replaceSearchTermAndSetDataAttribute(term.getAttribute(DATA_TERM) + " ");

  sendEventToTracking({
    san_SuggestAdoptTerm: searchTerm,
    san_SuggestAdoptPosition: position,
  });

  _unmarkFormForTracking(TRACK_SUGGEST_CLASS);
  _markFormForTracking(TRACK_MODIFIED_CLASS);

  searchField.focus();
}

export function _getNextActiveElement(key: number, current: any) {
  switch (key) {
    case ARROW_KEY_DOWN:
      return current.nextElementSibling;
    case ARROW_KEY_UP:
      return current.previousElementSibling;
    default:
      return null;
  }
}

export function _param(key: string): string {
  //TODO
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return encodeURIComponent(key) + "=" + encodeURIComponent(this[key]);
}

export function _hideLayerEventHandler(event: Event) {
  // TODO
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  if (!event.target.classList.contains("js_squirrel_ignore_close_suggest")) {
    hideLayer();
  }
}

export function _updateSuggestionLayer(suggestions: string): unknown {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  // TODO
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  this.innerHTML = suggestions;
  // TODO
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return this;
}

/**
 * Click on suggest entry
 * @param evt
 */
export function _clickSearchTermAction(evt: Event) {
  // TODO
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const target: Element = Boolean(this.querySelector).valueOf() ? this : evt.srcElement.offsetParent;
  const term: HTMLDivElement = target.querySelector(SEARCH_TERM_CLASS) as HTMLDivElement;

  window.o_util.event.stop(evt);

  _setSuggest(term.getAttribute(DATA_POS));
  const originalSearchTerm: AttributResponse = searchField.getAttribute("data-suggest-search-term");
  _replaceSearchTermAndSetDataAttribute(term.getAttribute(DATA_TERM));
  const words: AttributResponse = searchForm.getAttribute("data-suggestWordList");
  const wordLengths: AttributResponse = searchForm.getAttribute("data-suggestWordListCount");

  //Add tracking label here!
  _sendTrackingData({
    "wk.san_SearchType": "suggest",
    "wk.san_ActiveSearch": "true",
    "wk.san_SuggestPos": term.getAttribute(DATA_POS),
    "wk.san_SuggestSearchTerm": originalSearchTerm,
    san_InitialSearch: "true",
    san_Interaction: "initial_search",
    san_SuggestSearchTermLength: originalSearchTerm ? originalSearchTerm.length.toString() : null,
    san_SuggestListWordCount: wordLengths,
    san_SuggestList: words,
  });

  _unmarkFormForTracking(TRACK_MODIFIED_CLASS);
  _submitForm();
}

// TODO return type
export function _extractSuggestions(json: unknown): Suggestions {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const keywords = json.keywords;
  return (
    keywords.length && {
      keywords: keywords,
      suggestPos: coreUtils.counter(),
    }
  );
}

export function _encode(url: string | null | undefined, json: JSONQuery): string {
  return url + "?" + Object.keys(json).map(window.o_util.core.bind(json, _param)).join("&");
}

export function _fetchSuggestions(searchTerm: string): Promise<XMLHttpRequestProcessed> {
  return window.o_util.ajax.getJSON(
    _encode(suggestLayer.getAttribute(DATA_SUGGEST_URL), {
      q: searchTerm,
    })
  );
}

/**
 * Promises rendered suggestion layer for the given search term.
 *
 * @param searchTerm the search term
 * @return rendered suggestion layer
 */
export function _retrieveSuggestions(searchTerm: string): Promise<string> {
  return Promise.all([
    _fetchSuggestions(searchTerm).then(_suggestionResponseHandler),
    templates.get(TEMPLATE_NAME),
  ]).then(_renderSuggestions);
}

export function _closeLayer() {
  window.o_global.eventQBus.emit("squirrel.search.close");
  hideLayer();
}

export function _showLayer() {
  document.body.addEventListener("click", _hideLayerEventHandler);
  searchbarWrapper.classList.add(SEARCHBAR_WRAPPER_VISIBLE_SUGGESTIONS_CLASS);
  suggestLayer.style.display = "block";
}

export function _writeTrackingSuggestions() {
  const suggestions = [].slice
    .call(document.getElementsByClassName("js_squirrel_searchSuggest"))
    .map((st: HTMLElement): string => {
      return st.innerText.trim();
    });
  const words: string = suggestions.join("|");
  const wordLengths: string = suggestions
    .map((w: string): number => {
      return w.split(" ").length;
    })
    .join("|");

  searchForm.setAttribute("data-suggestWordList", words);
  searchForm.setAttribute("data-suggestWordListCount", wordLengths);
}

/**
 * Sends tracking data
 *
 * @param json encapsulating the data to track
 */
export function _sendTrackingData(json: TrackingData) {
  tracking.sendTrackingDataForNextPI(json);
}

/**
 * Needed for submit tracking within searchfield,js if term was taken by using the arrow
 */
export function _markFormForTracking(marker: string) {
  searchForm.classList.add(marker);
}

/**
 * Precautionary used by every action excepting a click on an arrow
 */
export function _unmarkFormForTracking(marker: string) {
  searchForm.classList.remove(marker);
}

/**
 * Used when clicking on a suggest to avoid sending get parameters
 */
export function _submitForm() {
  const searchFieldValue = stringUtils.escapeSpecials(searchField.value);
  const action: string = searchForm.getAttribute("action") + "/" + encodeURIComponent(searchFieldValue) + "/";

  globalUtils.setLocation(action);
}

/**
 * Update suggest position as global information
 *
 * @param searchField
 * @param suggestPos
 */
export function _setSuggest(suggestPos?: string | number | undefined | null, redirectTarget?: string | null) {
  searchForm.setAttribute(DATA_POS, suggestPos ? (suggestPos as string) : "");
  if (redirectTarget) {
    searchForm.setAttribute(DATA_TARGET, redirectTarget ? redirectTarget : "");
  } else {
    searchForm.removeAttribute(DATA_TARGET);
  }
}

/**
 * Update text within search field and send spell check tracking
 *
 * @param searchTerm
 */
export function _replaceSearchTerm(searchTerm: string | null) {
  if (searchTerm !== "" && searchTerm !== null) {
    searchField.value = searchTerm;
  }
}

/**
 * Update text within search field and sets data attribute
 *
 * @param searchTerm
 */
export function _replaceSearchTermAndSetDataAttribute(searchTerm: string | null) {
  if (searchTerm !== "" && searchTerm !== null) {
    searchField.value = searchTerm;
    searchField.setAttribute(DATA_SUGGEST_SEARCH_TERM, stringUtils.trimRight(searchTerm));
  }
}

export function _suggestionResponseHandler(xhr: XMLHttpRequestProcessed): Suggestions | Promise<unknown> {
  return xhr.status === 200
    ? _extractSuggestions(xhr.responseJSON) || Promise.reject(new Error("No suggestions to show"))
    : Promise.reject(new Error("received status code " + xhr.status));
}

/**
 * Renders the suggestions with the given template.
 *
 * @param results the input data
 */
export function _renderSuggestions(results: [unknown, unknown]): string {
  return Mustache.render(results[1] + "", results[0]);
}

/**
 * Find and show suggestions for given searchTerm. Checks cache before performing an ajax request.
 *
 * @param searchTerm
 */
export function _suggest(searchTerm: string) {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const suggestions = renderCache.get(searchTerm).then(window.o_util.core.bind(suggestLayer, _updateSuggestionLayer));

  _setSuggest(-1);
  suggestions.then(_showLayer).then(_writeTrackingSuggestions).then(undefined, hideLayer); // equivalent to catch(hidelayer) - looking at you jslint
}

/**
 * Used when pressing arrow down key
 * @param activeElement
 */
export function _updateSearchField(activeElement: HTMLLIElement) {
  const termElement: HTMLElement = activeElement.querySelector(SEARCH_TERM_CLASS) as HTMLElement;
  const redirectTarget: AttributResponse = termElement.getAttribute(DATA_TARGET);
  const term: AttributResponse = termElement
    ? termElement.getAttribute(DATA_TERM)
    : activeElement.getAttribute(DATA_TERM);
  const isContentSuggest = !!redirectTarget;
  if (Boolean(termElement).valueOf()) {
    _setSuggest(termElement.getAttribute(DATA_POS), isContentSuggest ? redirectTarget : null);
  }
  _replaceSearchTerm(term);
  _markFormForTracking(TRACK_SUGGEST_CLASS);
}

export function _updateActiveElement(previous: HTMLLIElement, next: HTMLLIElement) {
  if (previous) {
    previous.classList.remove("squirrel_searchSuggest--selected");
  }
  if (next) {
    next.classList.add("squirrel_searchSuggest--selected");
    _updateSearchField(next);
  }
}

export function _getInitiallyActiveElement(key: number): HTMLLIElement | null {
  const entries: NodeListOf<HTMLLIElement> = suggestLayer.querySelectorAll(".squirrel_searchSuggest");
  switch (key) {
    case ARROW_KEY_DOWN: //Down
      return entries[0];
    case ARROW_KEY_UP: //Up
      return entries[entries.length - 1];
    default:
      return null;
  }
}

/**
 * Retrieve key depending on browser type and call the associated action.
 *
 * Hint: In the past key 229 (unused code) was skipped, but due to errors the skip was
 * removed and replaced by searchField.value !== "". Now the suggest will be opened correctly.
 *
 * @param evt
 */
export function _handleKeyAction(evt: KeyboardEvent) {
  let searchTerm: string;
  let activeElement: HTMLLIElement;
  const key: number = Boolean(evt.which).valueOf() ? evt.which : evt.keyCode;
  switch (key) {
    case ARROW_KEY_UP:
    case ARROW_KEY_DOWN:
      evt.preventDefault();
      activeElement = suggestLayer.querySelector(".squirrel_searchSuggest--selected") as HTMLLIElement;
      _updateActiveElement(
        activeElement,
        activeElement ? _getNextActiveElement(key, activeElement) : _getInitiallyActiveElement(key)
      );
      break;
    case ESC_KEY:
      _closeLayer();
      break;
    default:
      searchTerm = stringUtils.trimLeft(searchField.value);
      searchField.setAttribute("data-suggest-search-term", searchTerm);
      // Do not re-suggest on empty terms or form submits (enter)
      if (searchTerm !== "" && key !== ENTER_KEY) {
        if (!headerElement.classList.contains(ADAPTED_SPRINGBREAK_CLASS)) {
          if (oldSearchTerm !== searchTerm) {
            _suggest(searchTerm);
          }
        } else {
          _suggest(searchTerm);
        }

        oldSearchTerm = searchTerm;
        _unmarkFormForTracking(TRACK_SUGGEST_CLASS);
      } else {
        hideLayer();
      }
  }
}

export function setOldSearchTerm(string: string) {
  oldSearchTerm = string;
}

/**
 * Hide and Reset
 */
export function hideLayer() {
  document.body.removeEventListener("click", _hideLayerEventHandler);
  searchbarWrapper.classList.remove(SEARCHBAR_WRAPPER_VISIBLE_SUGGESTIONS_CLASS);
  // TODO added !
  suggestLayer.style.display = "none";
  suggestLayer.innerHTML = "";
  suggestCache = {};
  renderCache.clear();
  oldSearchTerm = undefined;
}

/**
 * Used by jasmine tests
 */
export function reset() {
  // TODO added !
  searchField.value = "";
  hideLayer();

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  searchbarWrapper = null;
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  searchForm = null;
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  searchField = null;
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  suggestLayer = null;
  oldSearchTerm = "";
  suggestCache = {};
  renderCache.clear();
}

export function init() {
  const parentWrapper: HTMLDivElement = document.querySelector(SEARCH_CONTAINER_CLASS_SELECTOR) as HTMLDivElement;

  renderCache = new Cache(_retrieveSuggestions);
  if (!searchForm && parentWrapper) {
    searchbarWrapper = parentWrapper.querySelector(SEARCH_WRAPPER_CLASS_SELECTOR) as HTMLDivElement;
    searchForm = parentWrapper.querySelector(SEARCH_FORM_CLASS_SELECTOR) as HTMLFormElement;
    searchField = parentWrapper.querySelector(SEARCH_FIELD_CLASS_SELECTOR) as HTMLInputElement;
    suggestLayer = parentWrapper.querySelector(LAYER_CLASS_SELECTOR) as HTMLDivElement;
    headerElement = document.getElementsByClassName(headerElementClass)[0];

    // be safe during experiment
    if (suggestLayer) {
      window.o_util.event.delegate(LAYER_CLASS_SELECTOR, "click", SUGGEST_TERM_SELECTOR, _clickSearchTermAction);
      window.o_util.event.delegate(LAYER_CLASS_SELECTOR, "click", ARROW_CLASS_SELECTOR, _clickArrowAction);

      searchField.addEventListener("keyup", _handleKeyAction);
      searchField.addEventListener("focus", _focusSearchFieldAction);
      if (headerElement.classList.contains(ADAPTED_SPRINGBREAK_CLASS)) {
        searchField.addEventListener("click", _focusSearchFieldAction);
      }
    }
  }
}

export interface SearchSuggest {
  init: typeof init;
  hideLayer: typeof hideLayer;
  reset: typeof reset;
  setOldSearchTerm: typeof setOldSearchTerm;
}

export const searchSuggest = { init, hideLayer, reset, setOldSearchTerm };
